import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, lighten, styled } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker as MuiDatePicker, DatePickerProps as MuiDatePickerProps } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import type {
  PickerChangeHandlerContext,
  DateValidationError,
  FieldSelectedSections,
} from '@mui/x-date-pickers/models';
import dayjs, { Dayjs } from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import preParsePostFormat from 'dayjs/plugin/preParsePostFormat';
import 'dayjs/locale/de';
import 'dayjs/locale/ar';
import { useTranslation } from '@askporter/config-provider';
import { IconSize, Icon } from '../Icon';
import {
  claimEntireFieldSectionOnPointerDown,
  selectEntireFieldSectionOnPointerUp,
} from './selectEntireFieldSectionOnPointerUp';

dayjs.extend(localeData);
dayjs.extend(preParsePostFormat);

/**
 * Notifies the parent only for an explicit clear (`null`) or a valid Dayjs.
 * Incomplete/invalid mid-edit values are ignored so controlled parents (e.g. RJSF)
 * do not sync an empty value back and wipe the field.
 */
export const commitDateValue = (newValue: Dayjs | null, onChange: (value: string) => void): void => {
  if (newValue === null) {
    onChange('');
    return;
  }

  if (!newValue.isValid()) {
    return;
  }

  // Use 'en' locale for the output format to ensure ASCII digits (YYYY-MM-DD) regardless
  // of the display locale. RTL locales like Arabic use Arabic-Indic numerals in .format()
  // via the preParsePostFormat plugin, which would produce an unparseable date string.
  onChange(newValue.locale('en').format('YYYY-MM-DD'));
};

const isFocusInsideDatePicker = (root: HTMLElement | null, target: EventTarget | null): boolean => {
  if (!root || !(target instanceof Node)) {
    return false;
  }
  if (root.contains(target)) {
    return true;
  }
  if (target instanceof Element) {
    return Boolean(target.closest('.MuiPickersPopper-root, [role="dialog"]'));
  }
  return false;
};

/** Stable slot identity — inline slot components remount the field on every render and steal focus. */
const OpenPickerIcon = () => (
  <Icon size={IconSize.MD} folder="design-system/calendar" customStyles={{ pointerEvents: 'none' }} />
);

const datePickerSlots = {
  openPickerIcon: OpenPickerIcon,
};

interface DatePickerProps extends Omit<MuiDatePickerProps, 'onChange' | 'onAccept' | 'value' | 'maxDate' | 'minDate'> {
  value: string | null;
  onChange: (value: string) => void;
  /**
   * Called when the user accepts a value from the calendar (e.g. day click with closeOnSelect).
   * Receives '' on clear, or a valid YYYY-MM-DD string.
   */
  onAccept?: (value: string) => void;
  /**
   * Called when focus leaves the date field entirely (not when moving between day/month/year sections).
   * Receives the committed YYYY-MM-DD string (or '') so parents need not re-read form state.
   */
  onBlur?: (value: string) => void;
  label: string;
  maxDate?: string | null;
  minDate?: string | null;
  id?: string;
}

const StyledDatePicker = styled(MuiDatePicker)(({ theme }) => ({
  '.MuiFormControl-root': {
    marginTop: 0,
    marginBottom: 0,
  },
  '.MuiPickersInputBase-root': {
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    color: '#414651',
    WebkitTextFillColor: '#414651',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '20px',
    '&:focus-within': {
      boxShadow: `0 0 0 1px ${lighten(theme.palette.primary.main, 0.4)}, 0 0 0 4px ${lighten(
        theme.palette.primary.main,
        0.8,
      )}`,
      '&.Mui-error': {
        boxShadow: `0px 1px 2px 0px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px rgba(240, 68, 56, 0.24)`,
      },
      backgroundColor: '#fff',
      outline: 'none',
      borderRadius: '8px !important',
    },
    '& .MuiPickersSectionList-root': {
      padding: '12px 0',
      // Icon is on the left (row-reverse); keep the day section from sitting flush against it.
      marginLeft: '12px',
    },
    // Prefer whole-section highlight on click rather than a caret between digits/letters.
    '& [role="spinbutton"]': {
      userSelect: 'all',
      WebkitUserSelect: 'all',
    },
    flexDirection: 'row-reverse',
    '& .MuiInputAdornment-root': {
      marginLeft: 0,
      '& .MuiIconButton-root': {
        marginLeft: '-8px',
      },
    },
  },
}));

/**
 * Uses `defaultValue` + remount key instead of a controlled `value` while typing.
 * Updating controlled `value` on every section keystroke re-renders the field and drops focus
 * (day → month → year). Draft state lives in a ref until accept/blur commits to the parent.
 */
export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  onAccept,
  onBlur,
  label,
  maxDate,
  minDate,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);
  const [selectedSections, setSelectedSections] = useState<FieldSelectedSections>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const isFocusedRef = useRef(false);
  const draftRef = useRef<Dayjs | null>(value ? dayjs(value) : null);
  const lastCommittedValueRef = useRef<string | null>(value);
  const onChangeRef = useRef(onChange);
  const onAcceptRef = useRef(onAccept);
  const onBlurRef = useRef(onBlur);
  onChangeRef.current = onChange;
  onAcceptRef.current = onAccept;
  onBlurRef.current = onBlur;

  // Remount with a fresh defaultValue only when the parent value changes while not editing.
  useEffect(() => {
    if (isFocusedRef.current) {
      return;
    }
    if (value === lastCommittedValueRef.current) {
      return;
    }
    lastCommittedValueRef.current = value;
    draftRef.current = value ? dayjs(value) : null;
    setPickerKey((key) => key + 1);
  }, [value]);

  const commitDraft = (next: Dayjs | null, notifyAccept: boolean) => {
    draftRef.current = next;
    commitDateValue(next, (committed) => {
      lastCommittedValueRef.current = committed || null;
      onChangeRef.current(committed);
      if (notifyAccept) {
        onAcceptRef.current?.(committed);
      }
    });
  };

  const handleChange = (newValue: Dayjs | null) => {
    // No setState — avoids re-render/remount while MUI advances day → month → year.
    // Treat field edits as focused even if the container focusin was missed.
    isFocusedRef.current = true;
    draftRef.current = newValue;
  };

  /**
   * MUI X v8 field edits call setValue with default changeImportance `'accept'`, so `onAccept`
   * also fires when a day/month/year section completes while typing (source is not `'view'`).
   * Only treat calendar/view acceptance as onAccept for parents (e.g. holiday persist).
   */
  const handleAccept = (accepted: Dayjs | null, context: PickerChangeHandlerContext<DateValidationError>) => {
    if (context.source !== 'view') {
      return;
    }
    commitDraft(accepted, true);
  };

  const commitLeave = () => {
    if (!isFocusedRef.current) {
      return;
    }

    isFocusedRef.current = false;
    setSelectedSections(null);
    const previousCommittedValue = lastCommittedValueRef.current ?? '';
    let committedValue = '';
    commitDateValue(draftRef.current, (committed) => {
      committedValue = committed;
      // onAccept may have already committed this exact value; avoid re-notifying onChange.
      if (committed !== previousCommittedValue) {
        lastCommittedValueRef.current = committed || null;
        onChangeRef.current(committed);
      }
    });
    onBlurRef.current?.(committedValue);
  };

  const handleContainerBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    // Section auto-advance often has a null relatedTarget; leave detection for that case is
    // handled by the document focusin/pointerdown listeners below.
    if (!event.relatedTarget) {
      return;
    }
    if (isFocusInsideDatePicker(rootRef.current, event.relatedTarget)) {
      return;
    }
    commitLeave();
  };

  // When focus moves to another control (e.g. holiday description), commit the draft.
  // pointerdown alone misses keyboard tab; deferred blur alone misses some relatedTarget=null cases.
  useEffect(() => {
    const handleFocusIn = (event: FocusEvent) => {
      if (!isFocusedRef.current) {
        return;
      }
      if (isFocusInsideDatePicker(rootRef.current, event.target)) {
        return;
      }
      commitLeave();
    };

    const handlePointerDown = (event: PointerEvent) => {
      if (!isFocusedRef.current) {
        return;
      }
      if (isFocusInsideDatePicker(rootRef.current, event.target)) {
        return;
      }
      commitLeave();
    };

    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
    // Handlers only read refs; subscribe once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only listeners
  }, []);

  const { i18n } = useTranslation();
  const adapterLocale = i18n.language ? i18n.language.split('-')[0] : 'en';
  const format = adapterLocale === 'de' ? 'DD. MMM YYYY' : 'DD MMM YYYY';

  const slotProps = useMemo(
    () => ({
      textField: {
        inputProps: { 'aria-label': label },
        readOnly: props.readOnly,
        id: props.id,
        sx: { marginTop: 0, marginBottom: 0, width: '100%' },
      },
      openPickerButton: {
        'aria-label': label,
      },
      dialog: { 'aria-label': label },
      popper: { 'aria-label': label },
    }),
    [label, props.readOnly, props.id],
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
      <Box
        ref={rootRef}
        sx={{ display: 'inline-flex', width: '100%' }}
        onFocus={() => {
          isFocusedRef.current = true;
        }}
        onBlur={handleContainerBlur}
        onPointerDownCapture={(event) => {
          const sectionIndex = claimEntireFieldSectionOnPointerDown(event, rootRef.current);
          if (sectionIndex !== null) {
            isFocusedRef.current = true;
            setSelectedSections(sectionIndex);
          }
        }}
        onPointerUp={(event) => selectEntireFieldSectionOnPointerUp(event, rootRef.current)}
      >
        <StyledDatePicker
          {...props}
          key={pickerKey}
          defaultValue={value ? dayjs(value) : null}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          closeOnSelect
          onChange={handleChange}
          onAccept={handleAccept}
          selectedSections={selectedSections}
          onSelectedSectionsChange={setSelectedSections}
          disabled={props.disabled}
          format={format}
          slots={datePickerSlots}
          slotProps={slotProps}
          maxDate={maxDate ? dayjs(maxDate) : undefined}
          minDate={minDate ? dayjs(minDate) : undefined}
        />
      </Box>
    </LocalizationProvider>
  );
};
